import * as z from "zod";

export const SUPPORT_V2_MIGRATION_STEP_VALUES = [
  "INTEGRATIONS",
  "TAGS",
  "CONVERSATIONS",
  "SETTINGS_BACKFILL",
  "RULES_V2_TO_V3",
  "VIEWS_AND_FOLDERS",
  "MACROS",
] as const;

export const SupportV2MigrationStep = z.enum(SUPPORT_V2_MIGRATION_STEP_VALUES);
export type SupportV2MigrationStep = z.infer<typeof SupportV2MigrationStep>;

export const SupportV2MigrationRunStatus = z.enum([
  "completed",
  "completed_with_failures",
]);
export type SupportV2MigrationRunStatus = z.infer<
  typeof SupportV2MigrationRunStatus
>;

export const SupportV2MigrationRerunKey = z
  .string()
  .regex(/^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/);
export type SupportV2MigrationRerunKey = z.infer<
  typeof SupportV2MigrationRerunKey
>;

export const SUPPORT_V2_MIGRATION_DISPLAY_ORDER = [
  "INTEGRATIONS",
  "TAGS",
  "CONVERSATIONS",
  "SETTINGS_BACKFILL",
  "VIEWS_AND_FOLDERS",
  "MACROS",
  "RULES_V2_TO_V3",
] as const satisfies readonly SupportV2MigrationStep[];

export const SUPPORT_V2_MIGRATION_EXECUTION_ORDER = [
  "SETTINGS_BACKFILL",
  "TAGS",
  "VIEWS_AND_FOLDERS",
  "INTEGRATIONS",
  "CONVERSATIONS",
  "MACROS",
  "RULES_V2_TO_V3",
] as const satisfies readonly SupportV2MigrationStep[];

export const SUPPORT_V2_MIGRATION_STEP_LABELS = {
  INTEGRATIONS: "Integrations",
  TAGS: "Tags",
  CONVERSATIONS: "Conversations",
  SETTINGS_BACKFILL: "Settings",
  VIEWS_AND_FOLDERS: "Views and folders",
  MACROS: "Macros",
  RULES_V2_TO_V3: "Rules",
} as const satisfies Record<SupportV2MigrationStep, string>;

export const SUPPORT_V2_MIGRATION_STEP_OPTIONS =
  SUPPORT_V2_MIGRATION_DISPLAY_ORDER.map((value) => ({
    value,
    label: SUPPORT_V2_MIGRATION_STEP_LABELS[value],
  }));

const SUPPORT_V2_MIGRATION_STEP_DEPENDENCIES = {
  INTEGRATIONS: [],
  TAGS: [],
  CONVERSATIONS: ["INTEGRATIONS", "TAGS"],
  SETTINGS_BACKFILL: [],
  VIEWS_AND_FOLDERS: ["SETTINGS_BACKFILL", "TAGS"],
  MACROS: [],
  RULES_V2_TO_V3: [],
} as const satisfies Record<
  SupportV2MigrationStep,
  readonly SupportV2MigrationStep[]
>;

export interface SupportV2MigrationPlan {
  readonly isFullMigration: boolean;
  readonly steps: readonly SupportV2MigrationStep[];
}

export function getSupportV2MigrationStepDependencies(
  step: SupportV2MigrationStep,
): readonly SupportV2MigrationStep[] {
  return SUPPORT_V2_MIGRATION_STEP_DEPENDENCIES[step];
}

export function getSupportV2MigrationImplicitSteps(
  steps: Iterable<SupportV2MigrationStep>,
): readonly SupportV2MigrationStep[] {
  const implicitSteps = new Set<SupportV2MigrationStep>();
  const visitDependencies = (step: SupportV2MigrationStep) => {
    for (const dependency of getSupportV2MigrationStepDependencies(step)) {
      implicitSteps.add(dependency);
      visitDependencies(dependency);
    }
  };

  for (const step of steps) {
    visitDependencies(step);
  }

  return SUPPORT_V2_MIGRATION_DISPLAY_ORDER.filter((step) =>
    implicitSteps.has(step),
  );
}

export function expandSupportV2MigrationSteps(
  steps: Iterable<SupportV2MigrationStep>,
): readonly SupportV2MigrationStep[] {
  const expandedSteps = new Set<SupportV2MigrationStep>();
  const visit = (step: SupportV2MigrationStep) => {
    for (const dependency of getSupportV2MigrationStepDependencies(step)) {
      visit(dependency);
    }
    expandedSteps.add(step);
  };

  for (const step of steps) {
    visit(step);
  }

  return SUPPORT_V2_MIGRATION_EXECUTION_ORDER.filter((step) =>
    expandedSteps.has(step),
  );
}

export function normalizeSupportV2MigrationPlan(
  requestedSteps?: readonly SupportV2MigrationStep[],
): SupportV2MigrationPlan {
  if (!requestedSteps || requestedSteps.length === 0) {
    return {
      isFullMigration: true,
      steps: SUPPORT_V2_MIGRATION_EXECUTION_ORDER,
    };
  }

  const steps = expandSupportV2MigrationSteps(requestedSteps);
  const isFullMigration =
    steps.length === SUPPORT_V2_MIGRATION_EXECUTION_ORDER.length;

  return {
    isFullMigration,
    steps: isFullMigration ? SUPPORT_V2_MIGRATION_EXECUTION_ORDER : steps,
  };
}

export function getSupportV2MigrationWorkflowInputSteps(
  requestedSteps?: readonly SupportV2MigrationStep[],
): readonly SupportV2MigrationStep[] | undefined {
  const plan = normalizeSupportV2MigrationPlan(requestedSteps);
  return plan.isFullMigration ? undefined : plan.steps;
}

export function getSupportV2MigrationWorkflowId(
  teamId: string,
  requestedSteps?: readonly SupportV2MigrationStep[],
  options?: { readonly rerunKey?: SupportV2MigrationRerunKey },
): string {
  const plan = normalizeSupportV2MigrationPlan(requestedSteps);
  const stepSuffix = plan.isFullMigration
    ? ""
    : `-${[...plan.steps].sort().join("-").toLowerCase()}`;
  const baseWorkflowId = `migrate-team-to-support-v2-${teamId}${stepSuffix}`;
  if (!options?.rerunKey) {
    return baseWorkflowId;
  }
  const rerunKey = SupportV2MigrationRerunKey.parse(options.rerunKey);
  return `${baseWorkflowId}-rerun-${rerunKey}`;
}

interface SupportV2MigrationFailedCount {
  readonly failed: number;
}

export function getSupportV2MigrationRunStatus(
  summaries: Iterable<SupportV2MigrationFailedCount | undefined>,
): SupportV2MigrationRunStatus {
  for (const summary of summaries) {
    if (summary && summary.failed > 0) {
      return "completed_with_failures";
    }
  }
  return "completed";
}
